import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Text } from '@/components/gluestack/text';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { Skeleton } from '@/components/gluestack/skeleton';
import { Comment } from '@/models/Comment';
import { RenderHtml } from '@/components/ui/RenderHtml';
import { MediaView } from '@/components/ui/media/MediaView';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { useTranslation } from 'react-i18next';
import { useFormatCount } from '@/hooks/useFormatCount';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { ActionLink } from '~/components/ui/ActionLink';
import { LikeButton } from '@/components/domain/common/LikeButton';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { MemberListSheet } from '@/components/domain/common/MemberListSheet';
import { CommentContextMenu } from './CommentContextMenu';
import { Post } from '@/models/Post';
import { Photo } from '@/models/Photo';
import { Link } from 'expo-router';
import { getMemberPath } from '@/constants/routes';

interface CommentCardProps {
  comment?: Comment;
  parentPost?: Post;
  parentPhoto?: Photo;
  compact?: boolean;
  onPress?: () => void;
}

function CommentCardComponent({
  comment,
  parentPost,
  parentPhoto,
  compact = false,
  onPress,
}: CommentCardProps) {
  const { t } = useTranslation();
  const [showLikesSheet, setShowLikesSheet] = useState(false);
  const { formatCount } = useFormatCount();

  if (!comment) {
    return <CommentCardSkeleton />;
  }

  if (compact) {
    const Wrapper = onPress ? TouchableOpacity : View;
    return (
      <Wrapper
        {...(onPress ? { onPress, activeOpacity: 0.7 } : {})}
        className="mb-1.5 self-stretch">
        <HStack className="items-start gap-1">
          <ProfilePicture member={comment.Member} showTooltip size="xs" />
          <VStack className="ml-2 min-w-0 flex-1">
            <HStack className="items-baseline">
              <Link href={getMemberPath(comment.Member)} asChild>
                <TouchableOpacity activeOpacity={0.8}>
                  <Text size="sm" bold className="web:hover:underline">
                    {comment.Member.UserName}
                  </Text>
                </TouchableOpacity>
              </Link>
              <RelativeTime
                date={comment.DateCreated}
                className="ml-2 text-xs text-typography-500"
                short
                textOnly
              />
              {comment.IsEdited && (
                <Badge className="ml-2 rounded-xl" variant="solid" size="sm">
                  <BadgeText>{t('common.edited')}</BadgeText>
                </Badge>
              )}
            </HStack>
            {comment.Media ? (
              <View className="my-1 self-start">
                <MediaView item={comment.Media} maxWidth={160} />
              </View>
            ) : comment.HasText ? (
              <RenderHtml
                html={comment.TextHtmlNotEdited}
                size="sm"
                detectHashtags
                className="leading-tight text-typography-700"
                characterLimit={150}
                detectHttpsLinks
                noReadMoreButton
              />
            ) : null}
          </VStack>
        </HStack>
      </Wrapper>
    );
  }

  return (
    <HStack className="mb-5 flex-1 items-start">
      <ProfilePicture member={comment.Member} showTooltip className="h-10 w-10" />

      <VStack className="ml-3 flex-1">
        <HStack className="items-start">
          <VStack className="flex-1">
            <HStack className="items-baseline">
              <Link href={getMemberPath(comment.Member)} asChild>
                <TouchableOpacity activeOpacity={0.8}>
                  <Text bold className="web:hover:underline">
                    {comment.Member.UserName}
                  </Text>
                </TouchableOpacity>
              </Link>
              <RelativeTime
                date={comment.DateCreated}
                className="ml-2 text-ellipsis text-xs text-typography-500"
              />
              {comment.IsEdited && (
                <Badge className="ml-2 rounded-xl" variant="solid">
                  <BadgeText>{t('common.edited')}</BadgeText>
                </Badge>
              )}
            </HStack>
            {comment.Media ? (
              <View className="my-1 self-start">
                <MediaView item={comment.Media} />
              </View>
            ) : comment.HasText ? (
              <RenderHtml
                html={comment.TextHtmlNotEdited}
                size="sm"
                detectHashtags
                className="leading-tight text-typography-700"
                characterLimit={300}
                detectHttpsLinks
              />
            ) : null}
          </VStack>
          <CommentContextMenu comment={comment} parentPost={parentPost} parentPhoto={parentPhoto} />
        </HStack>

        <HStack className="mt-1 items-center gap-1">
          <LikeButton item={comment} itemType="comment" size={19} />

          {comment.LikesCount > 0 && (
            <>
              <ActionLink size="sm" onPress={() => setShowLikesSheet(true)}>
                {t('post.like', { count: comment.LikesCount }).replace(
                  comment.LikesCount.toString(),
                  formatCount(comment.LikesCount)
                )}
              </ActionLink>
              <ContextSheet
                key={`likes-sheet-comment-${comment.Id}`}
                isOpen={showLikesSheet}
                onClose={() => setShowLikesSheet(false)}
                contentComponent={MemberListSheet}
                shortTitle={t('likes.title')}
                title={t('likes.titleOnItem', {
                  itemType: t('common.comment'),
                  userName: comment.Member.UserName,
                })}
                height="lg"
                payload={{
                  listType: 'comment-likes',
                  comment,
                }}
              />
            </>
          )}
        </HStack>
      </VStack>
    </HStack>
  );
}

export const CommentCard = React.memo(CommentCardComponent);

function CommentCardSkeleton() {
  return (
    <HStack className="mb-6 mr-4 flex-1 items-start">
      <Skeleton className="h-10 w-10 rounded-full" />

      <VStack className="ml-3 flex-1">
        <HStack className="items-baseline">
          <Skeleton className="h-4 w-32 rounded-md" />
        </HStack>

        <VStack className="mt-1">
          <Skeleton className="h-4 w-full rounded-md" />
          <Skeleton className="mt-1 h-4 w-3/4 rounded-md" />
        </VStack>
      </VStack>
    </HStack>
  );
}
